// context/ColorProvider.tsx
'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { createBrowserSupabaseClient } from '@/utils/supabase/client';

interface ColorColors {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
    dark: string;
}

interface ColorContextType {
    colors: ColorColors;
    isLoading: boolean;
}

const ColorContext = createContext<ColorContextType>({
    colors: {
        primary: '#2f5d6c',    // valeurs par défaut
        secondary: '#f25c05',
        accent: '#d1e0e2',
        background: '#ffffff',
        text: '#1a1a1a',
        dark: '#17252A'
    },
    isLoading: true
});

export const ColorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [colors, setColors] = useState<ColorColors>({
        primary: '#2f5d6c',
        secondary: '#f25c05',
        accent: '#d1e0e2',
        background: '#ffffff',
        text: '#1a1a1a',
        dark: '#17252A'
    });
    const [isLoading, setIsLoading] = useState(true);
    const supabase = createBrowserSupabaseClient();

    // Fonction pour convertir une couleur hexadécimale en RGB
    function hexToRGB(hex: string): string {
        hex = hex.replace(/^#/, '');
        if (hex.length === 3) {
            hex = hex.split('').map(x => x + x).join('');
        }
        const intVal = parseInt(hex, 16);
        const r = (intVal >> 16) & 255;
        const g = (intVal >> 8) & 255;
        const b = intVal & 255;
        return `${r}, ${g}, ${b}`;
    }

    useEffect(() => {
        const fetchColorSettings = async () => {
            try {
                const { data, error } = await supabase
                    .from('theme_settings')
                    .select('*')
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .single();

                if (error) throw error;

                if (data) {
                    setColors({
                        primary: data.primary_color,
                        secondary: data.secondary_color,
                        accent: data.accent_color,
                        background: data.background_color,
                        text: data.text_color,
                        dark: data.dark_color
                    });

                    // Appliquer les couleurs aux variables CSS en convertissant en RGB
                    document.documentElement.style.setProperty('--color-primary', hexToRGB(data.primary_color));
                    document.documentElement.style.setProperty('--color-secondary', hexToRGB(data.secondary_color));
                    document.documentElement.style.setProperty('--color-accent', hexToRGB(data.accent_color));
                    document.documentElement.style.setProperty('--color-background', hexToRGB(data.background_color));
                    document.documentElement.style.setProperty('--color-background-secondary', hexToRGB(data.background_color_secondary));
                    document.documentElement.style.setProperty('--color-text', hexToRGB(data.text_color));
                    document.documentElement.style.setProperty('--color-dark', hexToRGB(data.dark_color));
                }
            } catch (err) {
                console.error('Erreur lors de la récupération des paramètres du thème:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchColorSettings();

        // Écouter les changements en temps réel
        const channel = supabase
            .channel('theme_settings_changes')
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'theme_settings' }, (payload) => {
                const newData = payload.new as any;
                setColors({
                    primary: newData.primary_color,
                    secondary: newData.secondary_color,
                    accent: newData.accent_color,
                    background: newData.background_color,
                    text: newData.text_color,
                    dark: newData.dark_color
                });

                // Mettre à jour les variables CSS avec les valeurs converties en RGB
                document.documentElement.style.setProperty('--color-primary', hexToRGB(newData.primary_color));
                document.documentElement.style.setProperty('--color-secondary', hexToRGB(newData.secondary_color));
                document.documentElement.style.setProperty('--color-accent', hexToRGB(newData.accent_color));
                document.documentElement.style.setProperty('--color-background', hexToRGB(newData.background_color));
                document.documentElement.style.setProperty('--color-background-secondary', hexToRGB(newData.background_color_secondary));
                document.documentElement.style.setProperty('--color-text', hexToRGB(newData.text_color));
                document.documentElement.style.setProperty('--color-dark', hexToRGB(newData.dark_color));
            })
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, []);

    return (
        <ColorContext.Provider value={{ colors, isLoading }}>
            {children}
        </ColorContext.Provider>
    );
};

export const useAppColor = () => useContext(ColorContext);
